import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField,
  CircularProgress, Switch, FormControlLabel, Grid, Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function ColorPicker({ label, value, onChange, hint }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="body2" fontWeight="bold" gutterBottom>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
               style={{ width: 52, height: 40, border: 'none', borderRadius: 4, cursor: 'pointer', padding: 2 }} />
        <TextField size="small" value={value} inputProps={{ maxLength: 7, style: { fontFamily: 'monospace' } }}
                   onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onChange(e.target.value); }}
                   sx={{ width: 120 }} />
      </Box>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Grid>
  );
}

export default function TournamentBrandingSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [title, setTitle] = useState('Torch Volleyball Tournament Registration');
  const [primary, setPrimary] = useState('#1a2940');
  const [secondary, setSecondary] = useState('#2c4a7c');
  const [bannerColor, setBannerColor] = useState('#ffffff');
  const [buttonBg, setButtonBg] = useState('#1a3a5c');
  const [buttonText, setButtonText] = useState('#ffffff');
  const [footerText, setFooterText] = useState('Tournament registration powered by Torch Volleyball');
  const [directorEmail, setDirectorEmail] = useState('admin@torchvolleyball.com');
  const [chipLogo, setChipLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoData, setLogoData] = useState('');
  const [logoMime, setLogoMime] = useState('');
  const [tLogoPreview, setTLogoPreview] = useState('');
  const [tLogoData, setTLogoData] = useState('');
  const [tLogoMime, setTLogoMime] = useState('');

  useEffect(() => { loadBranding(); }, []);

  async function loadBranding() {
    try {
      const snap = await getDoc(doc(db, 'settings', 'branding'));
      if (snap.exists()) {
        const b = snap.data();
        setTitle(b.title || 'Torch Volleyball Tournament Registration');
        setPrimary(b.primary || '#1a2940'); setSecondary(b.secondary || '#2c4a7c');
        setBannerColor(b.banner_color || '#ffffff'); setButtonBg(b.button_bg || '#1a3a5c');
        setButtonText(b.button_text || '#ffffff');
        setFooterText(b.footer || 'Tournament registration powered by Torch Volleyball');
        setDirectorEmail(b.director_email || 'admin@torchvolleyball.com');
        setChipLogo(b.chip_logo || false);
        if (b.logo_data) { setLogoPreview(`data:${b.logo_mime};base64,${b.logo_data}`); setLogoData(b.logo_data); setLogoMime(b.logo_mime); }
        if (b.tournament_logo_data) { setTLogoPreview(`data:${b.tournament_logo_mime};base64,${b.tournament_logo_data}`); setTLogoData(b.tournament_logo_data); setTLogoMime(b.tournament_logo_mime); }
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  function handleLogoFile(e, setPreview, setData, setMime) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const dataUrl = ev.target.result;
      setPreview(dataUrl);
      const [header, b64] = dataUrl.split(',');
      setData(b64); setMime(header.replace('data:','').replace(';base64',''));
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setSaved(false);
    try {
      await setDoc(doc(db, 'settings', 'branding'), {
        title, primary, secondary, banner_color: bannerColor, button_bg: buttonBg, button_text: buttonText,
        footer: footerText, director_email: directorEmail, chip_logo: chipLogo,
        logo_data: logoData, logo_mime: logoMime, tournament_logo_data: tLogoData, tournament_logo_mime: tLogoMime,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  const previewBg = `linear-gradient(135deg,${primary} 0%,${secondary} 100%)`;

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container><Typography variant="h4" fontWeight="bold">🎨 Branding Settings</Typography></Container>
      </Box>
      <Container maxWidth="md" sx={{ pb: 6 }}>
        {saved && <Alert severity="success" sx={{ mb: 3 }}>Branding saved! Refresh public pages to see changes.</Alert>}
        <form onSubmit={handleSubmit}>

          <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}><Typography fontWeight="bold">Site Title</Typography></Box>
            <CardContent sx={{ p: 3 }}>
              <TextField fullWidth label="Site Title" value={title} onChange={e => setTitle(e.target.value)}
                         helperText="Appears in the navbar and browser tab." />
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}><Typography fontWeight="bold">Colors</Typography></Box>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <ColorPicker label="Primary Color" value={primary} onChange={setPrimary} hint="Navbar, hero banner, footer" />
                <ColorPicker label="Secondary Color" value={secondary} onChange={setSecondary} hint="Gradient end color" />
                <ColorPicker label="Banner Font Color" value={bannerColor} onChange={setBannerColor} hint="Text in hero banner" />
                <ColorPicker label="Button Color" value={buttonBg} onChange={setButtonBg} hint="Primary button background" />
                <ColorPicker label="Button Text Color" value={buttonText} onChange={setButtonText} hint="Primary button text" />
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>Preview</Typography>
                <Box sx={{ background: previewBg, color: bannerColor, p: 2, borderRadius: 2, textAlign: 'center', fontWeight: 'bold', mb: 1.5 }}>
                  Navbar / Banner Preview
                </Box>
                <button type="button" style={{ background: buttonBg, color: buttonText, border: 'none', borderRadius: 6, padding: '8px 20px', fontWeight: 600, cursor: 'default' }}>
                  Button Preview
                </button>
              </Box>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}><Typography fontWeight="bold">Logo</Typography></Box>
            <CardContent sx={{ p: 3 }}>
              {logoPreview && <Box sx={{ mb: 2 }}><img src={logoPreview} alt="Logo" height={60} /></Box>}
              <Typography variant="body2" fontWeight="bold" gutterBottom>Navbar Logo</Typography>
              <input type="file" accept="image/*" style={{ display: 'block', marginBottom: 8 }}
                     onChange={e => handleLogoFile(e, setLogoPreview, setLogoData, setLogoMime)} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                PNG or JPG. Recommended height: 40–80px.
              </Typography>
              <Typography variant="body2" fontWeight="bold" gutterBottom>Tournament Card Logo</Typography>
              {tLogoPreview && <Box sx={{ mb: 1 }}><img src={tLogoPreview} alt="Tournament Logo" height={60} /></Box>}
              <input type="file" accept="image/*" style={{ display: 'block', marginBottom: 8 }}
                     onChange={e => handleLogoFile(e, setTLogoPreview, setTLogoData, setTLogoMime)} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Shown in tournament chips/cards. Falls back to navbar logo if not set.
              </Typography>
              <FormControlLabel control={<Switch checked={chipLogo} onChange={e => setChipLogo(e.target.checked)} />}
                                label="Use logo in tournament chips instead of trophy icon" />
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}><Typography fontWeight="bold">Footer Text</Typography></Box>
            <CardContent sx={{ p: 3 }}>
              <TextField fullWidth multiline rows={2} label="Footer Text" value={footerText}
                         onChange={e => setFooterText(e.target.value)} helperText="Appears at the bottom of every page." />
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}><Typography fontWeight="bold">Tournament Director</Typography></Box>
            <CardContent sx={{ p: 3 }}>
              <TextField fullWidth label="Director Email" type="email" value={directorEmail}
                         onChange={e => setDirectorEmail(e.target.value)}
                         helperText="Receives notifications for every registration change." />
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" startIcon={saving ? null : <SaveIcon />} disabled={saving}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Branding'}
            </Button>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin')}>Cancel</Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
}
